import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { User } from "../types/user.types";
import type { Center } from "../types/center.types";
import type { Patient } from "../types/patient.types";
import type { Variant, VariantOption } from "../types/earmold.types";
import _ from "lodash";
import {
	type Command,
	CommandStatus,
	CommandType,
} from "../types/command.types";
import { createCommand, updateCommand } from "../mutations/command.mutations";
import { fetchEarmoldVariants } from "../queries/earmold.queries";
import type { SavedFile } from "../types/files.types";
import { getReworkSelectionChange } from "./order.rework";

export enum NewOrderStep {
	CHOOSE_AUDIO_RESPONSIBLE = 0,
	CHOOSE_COMMAND_TYPE = 2,
	CHOOSE_EARPRINT_SHIPPING_TYPE = 3,
	CHOOSE_SETUP = 4,
	CHOOSE_SHIPPING = 5,
	CHOOSE_RENEW_REASON = 6,
	CHOOSE_RENEW_COMMANDS = 7,
	CHOOSE_RENEW_SETUP = 8,
	VALIDATION = 1000,
}

export enum NewOrderType {
	NEW = "NEW",
	REWORK = "REWORK",
}

export enum NewOrderEarSide {
	LEFT = "LEFT",
	RIGHT = "RIGHT",
}

export enum NewOrderEarprintShippingType {
	NATUS_OTOSCAN = "NATUS_OTOSCAN",
	DIGITAL = "DIGITAL",
	PHYSICAL = "PHYSICAL",
	REWORK_OLD_COMMAND = "REWORK_OLD_COMMAND",
}

export enum NewOrderRenewReason {
	RENEW = "RENEW",
	CHANGE_EARMOLD_TYPE = "CHANGE_EARMOLD_TYPE",
	CHANGE_CARACTERISTICS = "CHANGE_CARACTERISTICS",
	BROKEN = "BROKEN",
	LOST = "LOST",
	OTHER = "OTHER",
}

export type NewOrderForm = { [key: string]: VariantOption | undefined | null };

export interface OrderStoreState {
	/**
	 * STEP
	 */
	step: NewOrderStep;
	/**
	 * CENTER
	 */
	center?: Center;
	/**
	 * COMMAND
	 */
	command?: Command;
	/**
	 * AUDIO RESPONSIBLE
	 */
	audioResponsible?: User;
	/**
	 * CREATOR
	 */
	creator?: User;
	/**
	 * PATIENT
	 */
	patient?: Patient;
	/**
	 * EXTERNAL NUMBER ID
	 */
	externalNumberId?: string;
	/**
	 * COMMAND TYPE
	 */
	type?: NewOrderType;
	/**
	 * EARPRINT SHIPPING TYPE
	 */
	earprintShippingType?: NewOrderEarprintShippingType;
	/**
	 * DATE
	 */
	date?: Date;
	/**
	 * REWORK COMMAND
	 */
	reworkCommand?: Command;
	reworkCommandOld?: string;
	/**
	 * SETUP
	 */
	hasRightEar?: boolean;
	hasLeftEar?: boolean;
	earmold?: string;
	variants?: Variant[];
	rightEarForm: NewOrderForm;
	leftEarForm: NewOrderForm;
	formIndexes: number[];
	commentary?: string;
	leftFile?: SavedFile;
	rightFile?: SavedFile;
	additionalFiles?: SavedFile[];
	renewReason?: NewOrderRenewReason;
	updating?: boolean;
	showError?: boolean;
	isPriority?: boolean;
	shouldHydrateReworkSetup?: boolean;
}

interface OrderStoreActions {
	/**
	 * STEP
	 */
	setStep: (step: NewOrderStep) => void;
	getStep: () => NewOrderStep;
	goPreviousStep: () => void;
	canGoNextStep: () => boolean;
	goNextStep: () => void;
	/**
	 * CENTER
	 */
	setCenter: (center?: Center) => void;
	getCenter: () => Center | undefined;
	/**
	 * AUDIO RESPONSIBLE
	 */
	setAudioResponsible: (audio?: User) => void;
	getAudioResponsible: () => User | undefined;
	/**
	 * CREATOR
	 */
	setCreator: (user?: User) => void;
	/**
	 * PATIENT
	 */
	setPatient: (patient?: Patient) => void;
	/**
	 * EXTERNAL NUMBER ID
	 */
	setExternalNumberId: (externalNumberId?: string) => void;
	/**
	 * COMMAND TYPE
	 */
	setType: (type?: NewOrderType) => void;
	/**
	 * REWORK COMMAND
	 */
	setReworkCommand: (command?: string | Command) => void;
	/**
	 * EARPRINT SHIPPING TYPE
	 */
	setEarprintShippingType: (type?: NewOrderEarprintShippingType) => void;
	setFile: (side: NewOrderEarSide, file: SavedFile | undefined) => void;
	/**
	 * DATE
	 */
	setDate: (date: Date) => void;
	/**
	 * SETUP
	 */
	enableEar: (side: NewOrderEarSide, state?: boolean) => void;
	setEarmold: (earmold: string, variants?: Variant[]) => void;
	getVariantValue: (
		side: NewOrderEarSide,
		label: string,
	) => VariantOption | undefined | null;
	getForm: (size: NewOrderEarSide) => NewOrderForm | undefined;
	setVariantValue: (
		side: NewOrderEarSide,
		label: string,
		value: VariantOption,
	) => void;
	setCommentary: (commentary: string) => void;
	setAdditionalFiles: (files?: SavedFile[]) => void;
	isDisabled: (
		variant: Variant,
		option: VariantOption,
		form?: NewOrderForm,
	) => boolean;
	setRenewReason: (reason?: NewOrderRenewReason) => void;
	isRework: () => boolean;
	setIsUpdate: (updating?: boolean) => void;
	isUpdate: () => boolean;
	/**
	 * STORE ACTIONS
	 */
	reset: () => void;
	save: () => OrderStoreState;
	load: (state: OrderStoreState) => void;
	reload: () => void;
	saveDraft: (options?: {
		status?: CommandStatus;
		log?: boolean;
	}) => Promise<Command | undefined>;
	updateCommand: () => Promise<void>;
	fromCommand: (command: Command) => Promise<void>;
	reworkFromCommand: (command: Command) => void;
	copyMostFulfilled: () => void;
	copySide: (side: NewOrderEarSide) => void;
	setError: () => void;
	togglePriority: () => void;
	autoFormIndex: () => void;
	toggleFormIndex: (index: number) => void;
}

const initialState: OrderStoreState = {
	step: NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE,
	center: undefined,
	command: undefined,
	audioResponsible: undefined,
	creator: undefined,
	patient: undefined,
	externalNumberId: undefined,
	type: undefined,
	earprintShippingType: undefined,
	hasRightEar: undefined,
	hasLeftEar: undefined,
	earmold: undefined,
	rightEarForm: {},
	leftEarForm: {},
	formIndexes: [0],
	commentary: undefined,
	date: undefined,
	leftFile: undefined,
	rightFile: undefined,
	additionalFiles: undefined,
	renewReason: undefined,
	reworkCommand: undefined,
	reworkCommandOld: undefined,
	updating: false,
	showError: false,
	isPriority: false,
	shouldHydrateReworkSetup: false,
};

export const useNewOrderStore = create<OrderStoreState & OrderStoreActions>()(
	devtools(
		persist(
			(set, get) => {
				const isEarFormValid = (form: NewOrderForm | undefined) => {
					if (form === undefined) {
						return false;
					}

					const variants = get().variants;
					if (!Array.isArray(variants)) {
						return false;
					}

					for (const variant of variants) {
						const hasAtLeastOneOptionEnabled = variant.options.data.some(
							(option) => get().isDisabled(variant, option, form) === false,
						);
						if (!hasAtLeastOneOptionEnabled) {
							continue;
						}

						const value = form[variant.id];
						if (value === undefined || value === null) {
							return false;
						}
					}

					return true;
				};

				const isSetupFormValid = () => {
					if (get().earmold === undefined) {
						return false;
					}

					if (get().hasLeftEar) {
						if (
							get().earprintShippingType ===
								NewOrderEarprintShippingType.DIGITAL &&
							get().leftFile === undefined
						) {
							return false;
						}
						if (!isEarFormValid(get().leftEarForm)) {
							return false;
						}
					}

					if (get().hasRightEar) {
						if (
							get().earprintShippingType ===
								NewOrderEarprintShippingType.DIGITAL &&
							get().rightFile === undefined
						) {
							return false;
						}
						if (!isEarFormValid(get().rightEarForm)) {
							return false;
						}
					}

					return get().hasLeftEar === true || get().hasRightEar === true;
				};

				const sanitizeEarForm = (
					form: NewOrderForm | undefined,
					variants: Variant[] | undefined,
				): NewOrderForm => {
					if (!Array.isArray(variants)) {
						return {};
					}

					const sanitizedForm: NewOrderForm = {};
					for (const variant of variants) {
						const key = variant.id.toString();
						const previousValue = form?.[key];
						const enabledOptions = variant.options.data.filter(
							(option) =>
								get().isDisabled(variant, option, sanitizedForm) === false,
						);

						if (enabledOptions.length === 0) {
							sanitizedForm[key] = null;
							continue;
						}

						if (
							previousValue &&
							typeof previousValue === "object" &&
							"uuid" in previousValue &&
							typeof previousValue.uuid === "string" &&
							enabledOptions.some((option) => option.uuid === previousValue.uuid)
						) {
							sanitizedForm[key] = previousValue;
							continue;
						}

						sanitizedForm[key] = undefined;
					}

					return sanitizedForm;
				};

				return {
					...initialState,
				setStep: (step: NewOrderStep) => {
					set(() => ({ step }));
				},
				getStep: () => get().step,
				togglePriority: () => {
					set(() => ({ isPriority: !get().isPriority }));
				},
				goPreviousStep: () => {
					switch (get().step) {
						case NewOrderStep.CHOOSE_COMMAND_TYPE: {
							set(() => ({
								step: NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE,
							}));
							break;
						}
						case NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE: {
							if (get().type === NewOrderType.NEW) {
								set(() => ({
									step: NewOrderStep.CHOOSE_COMMAND_TYPE,
								}));
							}
							if (get().type === NewOrderType.REWORK) {
								set(() => ({
									step: NewOrderStep.CHOOSE_COMMAND_TYPE,
								}));
							}
							break;
						}
						case NewOrderStep.CHOOSE_SETUP: {
							set(() => ({
								step: NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE,
							}));
							break;
						}
						case NewOrderStep.CHOOSE_SHIPPING: {
							set(() => ({ step: NewOrderStep.CHOOSE_SETUP }));
							break;
						}
						case NewOrderStep.VALIDATION: {
							set(() => ({ step: NewOrderStep.CHOOSE_SHIPPING }));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_REASON: {
							set(() => ({
								step: NewOrderStep.CHOOSE_COMMAND_TYPE,
							}));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_COMMANDS: {
							set(() => ({
								step: NewOrderStep.CHOOSE_RENEW_REASON,
							}));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_SETUP: {
							set(() => ({
								step: NewOrderStep.CHOOSE_RENEW_COMMANDS,
							}));
							break;
						}
					}
				},
				canGoNextStep: () => {
					switch (get().step) {
						case NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE: {
							return (
								get().audioResponsible !== undefined &&
								get().patient !== undefined
							);
						}
						case NewOrderStep.CHOOSE_COMMAND_TYPE: {
							return get().type !== undefined;
						}
						case NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE: {
							return get().earprintShippingType !== undefined;
						}
						case NewOrderStep.CHOOSE_SETUP: {
							return isSetupFormValid();
						}
						case NewOrderStep.CHOOSE_SHIPPING: {
							return true;
						}
						case NewOrderStep.VALIDATION: {
							return isSetupFormValid();
						}
						case NewOrderStep.CHOOSE_RENEW_REASON: {
							return get().renewReason !== undefined;
						}
						case NewOrderStep.CHOOSE_RENEW_COMMANDS: {
							return (
								get().reworkCommand !== undefined ||
								(get().reworkCommandOld !== undefined &&
									get().reworkCommandOld?.trim() !== "")
							);
						}
						case NewOrderStep.CHOOSE_RENEW_SETUP: {
							return isSetupFormValid();
						}
						default:
							return false;
					}
				},
				goNextStep: () => {
					switch (get().step) {
						case NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE: {
							set(() => ({
								step: NewOrderStep.CHOOSE_COMMAND_TYPE,
							}));
							break;
						}
						case NewOrderStep.CHOOSE_COMMAND_TYPE: {
							if (get().type === NewOrderType.NEW) {
								set(() => ({
									step: NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE,
								}));
							}
							if (get().type === NewOrderType.REWORK) {
								set(() => ({
									step: NewOrderStep.CHOOSE_RENEW_REASON,
								}));
							}
							break;
						}
						case NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE: {
							set(() => ({ step: NewOrderStep.CHOOSE_SETUP }));
							break;
						}
						case NewOrderStep.CHOOSE_SETUP: {
							set(() => ({ step: NewOrderStep.CHOOSE_SHIPPING }));
							break;
						}
						case NewOrderStep.CHOOSE_SHIPPING: {
							set(() => ({ step: NewOrderStep.VALIDATION }));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_REASON: {
							set(() => ({
								step: NewOrderStep.CHOOSE_RENEW_COMMANDS,
							}));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_COMMANDS: {
							set(() => ({
								step: NewOrderStep.CHOOSE_RENEW_SETUP,
							}));
							break;
						}
						case NewOrderStep.CHOOSE_RENEW_SETUP: {
							set(() => ({ step: NewOrderStep.CHOOSE_SHIPPING }));
							break;
						}
					}
				},
				setCenter: (center?: Center) => {
					set(() => ({
						center,
						patient: undefined,
						audioResponsible: undefined,
					}));
				},
				getCenter: () => get().center,
				setAudioResponsible: (audio?: User) => {
					set(() => ({ audioResponsible: audio }));
				},
				setCreator: (user?: User) => {
					set(() => ({ creator: user }));
				},
				getAudioResponsible: () => get().audioResponsible,
				setPatient: (patient?: Patient) => {
					set(() => ({ patient }));
				},
				setExternalNumberId: (externalNumberId?: string) =>
					set(() => ({ externalNumberId })),
				setType: (type?: NewOrderType) => {
					if (type !== NewOrderType.REWORK) {
						set(() => ({
							reworkCommand: undefined,
							reworkCommandOld: undefined,
						}));
					}
					set(() => ({ type }));
				},
				setEarprintShippingType: (type?: NewOrderEarprintShippingType) =>
					set(() => ({ earprintShippingType: type })),
				setFile: (side: NewOrderEarSide, file: SavedFile | undefined) => {
					if (side === NewOrderEarSide.LEFT) {
						set(() => ({ leftFile: file }));
					}
					if (side === NewOrderEarSide.RIGHT) {
						set(() => ({ rightFile: file }));
					}
				},
				enableEar: (side: NewOrderEarSide, state?: boolean) => {
					if (side === NewOrderEarSide.LEFT) {
						set(() => ({ hasLeftEar: state }));
					}
					if (side === NewOrderEarSide.RIGHT) {
						set(() => ({ hasRightEar: state }));
					}
				},
				setEarmold: (earmold: string, variants?: Variant[]) => {
					const form = variants
						?.map((v) => v.id)
						?.reduce((p, v) => ({ ...p, [v]: undefined }), {});
					const indexes = variants?.reduce(
						(acc, variant, index) => {
							const hasAtLeastOneOptionEnabled = variant.options.data.some(
								(option) => get().isDisabled(variant, option, form) === false,
							);
							if (hasAtLeastOneOptionEnabled) {
								acc.push(index + 1);
							}
							return acc;
						},
						[] as Array<number>,
					);
					/** Set variant and reset right, and left forms */
					set(() => ({
						earmold,
						variants,
						formIndexes: indexes ?? [],
						rightEarForm: form ?? {},
						leftEarForm: form ?? {},
					}));
				},
				getVariantValue: (side: NewOrderEarSide, label: string) => {
					switch (side) {
						case NewOrderEarSide.LEFT:
							return get().leftEarForm[label];
						case NewOrderEarSide.RIGHT:
							return get().rightEarForm[label];
						default:
							return undefined;
					}
				},
				getForm: (side: NewOrderEarSide) => {
					switch (side) {
						case NewOrderEarSide.LEFT:
							return get().leftEarForm;
						case NewOrderEarSide.RIGHT:
							return get().rightEarForm;
						default:
							return undefined;
					}
				},
				setError: () => {
					set({ showError: true });
				},
				setVariantValue: (
					side: NewOrderEarSide,
					label: string,
					value: VariantOption,
				) => {
					const canCopyValueToOtherSide = (targetForm: NewOrderForm) => {
						const variants = get().variants;
						if (!Array.isArray(variants)) {
							return false;
						}

						const variant = variants.find(
							(variant) => variant.id.toString() === label,
						);
						if (variant === undefined) {
							return false;
						}

						const option = variant.options.data.find(
							(option) => option.uuid === value.uuid,
						);
						if (option === undefined) {
							return false;
						}

						return get().isDisabled(variant, option, targetForm) === false;
					};

					switch (side) {
						case NewOrderEarSide.LEFT: {
							// 1. Get current state
							const copyLeftEarForm = get().leftEarForm;
							const copyRightEarForm = get().rightEarForm;
							// 2. Set variant value
							copyLeftEarForm[label] = value;
							if (
								(copyRightEarForm[label] === undefined ||
									copyRightEarForm[label] === null) &&
								canCopyValueToOtherSide(copyRightEarForm)
							) {
								copyRightEarForm[label] = value;
							}
							// 3. Invalidate all later choices
							const invalidatedCopyLeftEarForm = Object.fromEntries(
								Object.entries(copyLeftEarForm).map((data) => {
									const key = data[0];
									const childValue = data[1];
									const id = Number.parseInt(key);
									if (id > Number.parseInt(label)) {
										const variants = get().variants;
										if (Array.isArray(variants)) {
											const variant = variants.find(
												(variant) => variant.id === id,
											);
											if (variant !== undefined) {
												const optionsDisabled = variant?.options.data.map(
													(option) =>
														get().isDisabled(variant, option, copyLeftEarForm),
												);
												// In this case, no options can be selected by the user.
												if (_.every(optionsDisabled)) {
													return [key, null];
												}

												//
												// Option should be set to undefined, only if the .
												//
												const conditions = variants.find(
													(variant) => variant.id.toString() === key,
												)?.options?.conditions;
												if (!conditions || conditions.length === 0) {
													return [key, childValue];
												}

												const eraseMe = conditions
													.flatMap((vc) =>
														vc.conditions.map((c) => {
															return (
																c.variantId !== null &&
																c.variantId.toString() === label
															);
														}),
													)
													.some((c) => c === true);
												if (eraseMe) {
													return [key, undefined];
												}

												return [key, childValue];
											} else {
												return [key, undefined];
											}
										} else {
											return [key, undefined];
										}
									}
									return [key, childValue];
								}),
							);
							// 4. Set store
							return set(() => ({
								leftEarForm: invalidatedCopyLeftEarForm,
								rightEarForm: get().rightEarForm,
							}));
						}
						case NewOrderEarSide.RIGHT: {
							// 1. Get current state
							const copyRightEarForm = get().rightEarForm;
							const copyLeftEarForm = get().leftEarForm;
							// 2. Set variant value
							copyRightEarForm[label] = value;
							if (
								(copyLeftEarForm[label] === undefined ||
									copyLeftEarForm[label] === null) &&
								canCopyValueToOtherSide(copyLeftEarForm)
							) {
								copyLeftEarForm[label] = value;
							}
							// 3. Invalidate all later choices
							const invalidatedCopyRightEarForm = Object.fromEntries(
								Object.entries(copyRightEarForm).map((data) => {
									const key = data[0];
									const childValue = data[1];
									const id = Number.parseInt(key, 10);
									if (id > Number.parseInt(label, 10)) {
										const variants = get().variants;
										if (Array.isArray(variants)) {
											const variant = variants.find(
												(variant) => variant.id === id,
											);
											if (variant !== undefined) {
												const optionsDisabled = variant?.options.data.map(
													(option) =>
														get().isDisabled(variant, option, copyRightEarForm),
												);
												// In this case, no options can be selected by the user.
												if (_.every(optionsDisabled)) {
													return [key, null];
												}

												//
												// Option should be set to undefined, only if the .
												//
												const conditions = variants.find(
													(variant) => variant.id.toString() === key,
												)?.options?.conditions;
												if (!conditions || conditions.length === 0) {
													return [key, childValue];
												}

												const eraseMe = conditions
													.flatMap((vc) =>
														vc.conditions.map((c) => {
															return (
																c.variantId !== null &&
																c.variantId.toString() === label
															);
														}),
													)
													.some((c) => c === true);
												if (eraseMe) {
													return [key, undefined];
												}

												return [key, childValue];
											} else {
												return [key, undefined];
											}
										} else {
											return [key, undefined];
										}
									}
									return [key, childValue];
								}),
							);
							// 4. Set store
							return set(() => ({
								rightEarForm: invalidatedCopyRightEarForm,
								leftEarForm: get().leftEarForm,
							}));
						}
						default:
							return undefined;
					}
				},
				setCommentary: (commentary: string) => set(() => ({ commentary })),
				isDisabled: (
					variant: Variant,
					option: VariantOption,
					form?: NewOrderForm,
				) => {
					if (form === undefined) {
						return false;
					}
					let flagAffectedByAtLeastOneCondition = false;
					for (const condition of variant.options.conditions) {
						// 1. Validate if option is affected by condition
						if (option && !condition.targets.includes(option.uuid)) {
							continue;
						}
						flagAffectedByAtLeastOneCondition = true;
						// 2.0 Apply condition
						// 2.1 AND
						const shouldAllBeTrue = [];
						for (const subcondition of condition.conditions) {
							// If condition as no variant linked to it
							if (subcondition.variantId === null) {
								continue;
							}
							// If user has not select an option yet for this feature
							if (!(subcondition.variantId in form)) {
								continue;
							}
							const subconditionOption = form[subcondition.variantId];
							switch (subcondition.operator) {
								case "==": {
									shouldAllBeTrue.push(
										subcondition.values.findIndex(
											(value) =>
												subconditionOption && value === subconditionOption.uuid,
										) !== -1,
									);
									break;
								}
								case "!=": {
									shouldAllBeTrue.push(
										subcondition.values.findIndex(
											(value) =>
												subconditionOption && value === subconditionOption.uuid,
										) === -1,
									);
									break;
								}
								default:
									throw Error("unknown operator");
							}
						}
						if (_.every(shouldAllBeTrue)) {
							return false;
						}
					}
					// 0. Not affected by any condition, will always be displayed
					return flagAffectedByAtLeastOneCondition;
				},
				reset: () => set(initialState),
				saveDraft: async (options?: {
					status?: CommandStatus;
					log?: boolean;
				}) => {
					const command = get().command;
					let type: CommandType = CommandType.POSTE;
					switch (get().earprintShippingType) {
						case NewOrderEarprintShippingType.DIGITAL:
							type = CommandType.THREED;
							break;
						case NewOrderEarprintShippingType.NATUS_OTOSCAN:
							type = CommandType.OTOSCAN;
							break;
						case NewOrderEarprintShippingType.PHYSICAL:
							type = CommandType.POSTE;
							break;
						default:
							break;
					}
					const draftCopy = {
						...get(),
						shouldHydrateReworkSetup: undefined,
					};
					if (get().isRework()) {
						type = CommandType.REWORK;
					}
					if (!draftCopy.hasLeftEar) {
						draftCopy.leftEarForm = {};
						draftCopy.hasLeftEar = false;
					}
					if (!draftCopy.hasRightEar) {
						draftCopy.rightEarForm = {};
						draftCopy.hasRightEar = false;
					}
					if (command === undefined) {
						// Create command
						// 0. Extract variables
						const center = get().center;
						const patient = get().patient;
						// 1. Assert on variables
						if (center === undefined) {
							throw "Center is mandatory";
						}
						const command = await createCommand({
							centerId: center.id,
							patientId: patient?.id ?? undefined,
							status: options?.status ?? CommandStatus.DRAFT,
							type: type,
							draft: draftCopy,
							shippingAddressId: center.shippingAddressId,
							billingAddressId: center.enterprise?.addressId,
							externalId: get().externalNumberId,
							creatorId: get().creator?.id,
							managerId: get().audioResponsible?.id,
						});
						set(() => ({ command }));
						return command;
					} else {
						// 0. Extract variables
						const center = get().center;
						const patient = get().patient;
						if (center === undefined) {
							throw "Center is mandatory";
						}
						const updatedCommand = await updateCommand({
							id: command.id,
							centerId: center.id,
							patientId: patient?.id ?? undefined,
							status: options?.status,
							type: type,
							draft: { ...draftCopy, command: undefined },
							shippingAddressId: center.shippingAddressId,
							billingAddressId: center.enterprise?.addressId,
							externalId: get().externalNumberId,
							creatorId: get().creator?.id,
							managerId: get().audioResponsible?.id,
							log: options?.log,
						});
						set(() => ({ command: updatedCommand }));
						return updatedCommand;
					}
				},
				updateCommand: async () => {
					const command = get().command;
					let type: CommandType = CommandType.POSTE;
					switch (get().earprintShippingType) {
						case NewOrderEarprintShippingType.DIGITAL:
							type = CommandType.THREED;
							break;
						case NewOrderEarprintShippingType.NATUS_OTOSCAN:
							type = CommandType.OTOSCAN;
							break;
						case NewOrderEarprintShippingType.PHYSICAL:
							type = CommandType.POSTE;
							break;
						default:
							break;
					}
					if (get().isRework()) {
						type = CommandType.REWORK;
					}
					if (command !== undefined) {
						// 0. Extract variables
						const center = get().center;
						const patient = get().patient;
						if (center === undefined) {
							throw "Center is mandatory";
						}
						const updatedCommand = await updateCommand({
							id: command.id,
							centerId: center.id,
							patientId: patient?.id ?? undefined,
							type: type,
							draft: {
								...get(),
								shouldHydrateReworkSetup: undefined,
							},
							shippingAddressId: center.shippingAddressId,
							billingAddressId: center.enterprise?.addressId,
							creatorId: get().creator?.id,
							managerId: get().audioResponsible?.id,
						});
						set(() => ({ command: updatedCommand }));
					}
				},
				setDate: (date: Date) => {
					set(() => ({ date }));
				},
				setAdditionalFiles: (files?: SavedFile[]) => {
					set(() => ({ additionalFiles: files }));
				},
				save: () => {
					return get();
				},
				load: (state: OrderStoreState) => {
					// TODO be sure this is not breaking
					if (state.command?.status === CommandStatus.DRAFT && state.variants) {
						if (state.hasRightEar) {
							const rightEarForm: NewOrderForm = {};
							for (const variant of state.variants) {
								rightEarForm[variant.id] = state.rightEarForm[variant.id];
							}
							state.rightEarForm = rightEarForm;
						}
						if (state.hasLeftEar) {
							const leftEarForm: NewOrderForm = {};
							for (const variant of state.variants) {
								leftEarForm[variant.id] = state.leftEarForm[variant.id];
							}
							state.leftEarForm = leftEarForm;
						}
					}
					state.shouldHydrateReworkSetup = false;
					get().reset();
					set(() => state);
				},
				reload: () => {
					const state = get();
					get().load(state);
				},
				setReworkCommand: (command?: string | Command) => {
					const change =
						typeof command === "string"
							? getReworkSelectionChange({
									currentReworkCommand: get().reworkCommand,
									currentReworkCommandOld: get().reworkCommandOld,
									nextReworkCommandOld: command,
								})
							: getReworkSelectionChange({
									currentReworkCommand: get().reworkCommand,
									currentReworkCommandOld: get().reworkCommandOld,
									nextReworkCommand: command,
								});
					if (command === undefined) {
						set(() => ({
							reworkCommand: undefined,
							reworkCommandOld: undefined,
							isPriority: false,
							shouldHydrateReworkSetup: false,
						}));
					} else if (typeof command === "string") {
						if (change.hasChanged === false) {
							set(() => ({
								reworkCommand: undefined,
								reworkCommandOld: command,
								isPriority: false,
								shouldHydrateReworkSetup: false,
							}));
							return;
						}
						set(() => ({
							reworkCommand: undefined,
							reworkCommandOld: command,
							earprintShippingType:
								NewOrderEarprintShippingType.REWORK_OLD_COMMAND,
							earmold: undefined,
							leftEarForm: {},
							rightEarForm: {},
							isPriority: false,
							shouldHydrateReworkSetup: change.shouldHydrateReworkSetup,
						}));
					} else {
						if (change.hasChanged === false) {
							set(() => ({
								reworkCommand: command,
								reworkCommandOld: undefined,
								isPriority: false,
								shouldHydrateReworkSetup: false,
							}));
							return;
						}
						set(() => ({
							reworkCommand: command,
							reworkCommandOld: undefined,
							hasLeftEar: command.draft?.hasLeftEar,
							hasRightEar: command.draft?.hasRightEar,
							leftEarForm: command.draft?.leftEarForm,
							rightEarForm: command.draft?.rightEarForm,
							isPriority: false,
							shouldHydrateReworkSetup: change.shouldHydrateReworkSetup,
						}));
					}
				},
				setRenewReason: (reason?: NewOrderRenewReason) => {
					set(() => ({ renewReason: reason }));
				},
				fromCommand: async (command: Command) => {
					const draft = command.draft;
					const draftEarmoldId = draft?.earmold;
					let variants = draft?.variants;

					if (draftEarmoldId !== undefined && draftEarmoldId !== null) {
						try {
							const fetchedVariants = await fetchEarmoldVariants(draftEarmoldId);
							if (Array.isArray(fetchedVariants)) {
								variants = fetchedVariants;
							}
						} catch (_error) {}
					}

					set(() => ({
						earmold:
							draftEarmoldId !== undefined && draftEarmoldId !== null
								? draftEarmoldId.toString()
								: undefined,
						variants,
						leftEarForm: sanitizeEarForm(draft?.leftEarForm, variants),
						rightEarForm: sanitizeEarForm(draft?.rightEarForm, variants),
						leftFile: draft?.leftFile,
						rightFile: draft?.rightFile,
						shouldHydrateReworkSetup: false,
					}));
				},
				reworkFromCommand: (command: Command) => {
					const draft = command.draft;
					set(() => ({
						...draft,
						type: NewOrderType.REWORK,
						isPriority: false,
						reworkCommand: command,
						command: undefined,
						date: undefined,
						step: NewOrderStep.CHOOSE_RENEW_REASON,
						shouldHydrateReworkSetup: false,
					}));
				},
				isRework: () => {
					return (
						get().reworkCommand !== undefined ||
						get().reworkCommandOld !== undefined
					);
				},
				setIsUpdate: (updating?: boolean) => {
					set(() => ({ updating }));
				},
				isUpdate: () => {
					return get().updating === true;
				},
				autoFormIndex: () => {
					const form = get().rightEarForm ?? get().leftEarForm;
					const variants = get().variants;
					if (variants === undefined || form === undefined) return;

					const indexes = variants?.reduce(
						(acc, variant, index) => {
							const hasAtLeastOneOptionEnabled = variant.options.data.some(
								(option) => get().isDisabled(variant, option, form) === false,
							);
							const hasValue = form[variant.id] !== undefined;
							if (hasAtLeastOneOptionEnabled && !hasValue) {
								acc.push(index + 1);
							}
							return acc;
						},
						[] as Array<number>,
					);

					set(() => ({ formIndexes: indexes }));
				},
				toggleFormIndex: (index: number) => {
					const indexes = get().formIndexes;

					if (indexes === undefined) {
						set(() => ({ formIndexes: [index] }));
						return;
					}
					const found = indexes.find((i) => i === index);
					if (found) {
						set(() => ({
							formIndexes: indexes.filter((i) => i !== index),
						}));
					} else {
						set(() => ({
							formIndexes: [...indexes, index],
						}));
					}
				},
				copySide: (side: NewOrderEarSide) => {
					if (side === NewOrderEarSide.LEFT) {
						set(() => ({
							hasRightEar: true,
							rightEarForm: { ...get().leftEarForm },
						}));
					} else if (side === NewOrderEarSide.RIGHT) {
						set(() => ({
							hasLeftEar: true,
							leftEarForm: { ...get().rightEarForm },
						}));
					}
				},
				copyMostFulfilled: () => {
					const hasLeft = get().hasLeftEar;
					const hasRight = get().hasRightEar;
					if (hasLeft && !hasRight) {
						// 0. Enable right and copy left to right
						set(() => ({
							hasRightEar: true,
							rightEarForm: { ...get().leftEarForm },
						}));
					} else if (!hasLeft && hasRight) {
						// 0. Enable right and copy left to right
						set(() => ({
							hasLeftEar: true,
							leftEarForm: { ...get().rightEarForm },
						}));
					} else {
						const countLeft = Object.values(get().leftEarForm).reduce(
							(p, v) => (v === undefined ? p : p + 1),
							0,
						);
						const countRight = Object.values(get().rightEarForm).reduce(
							(p, v) => (v === undefined ? p : p + 1),
							0,
						);
						if (countLeft <= countRight) {
							set(() => ({
								leftEarForm: { ...get().rightEarForm },
							}));
						} else {
							set(() => ({
								rightEarForm: { ...get().leftEarForm },
							}));
						}
					}
				},
				};
			},
			{
				name: "order-store",
				version: 1,
				getStorage: () => localStorage,
			},
		),
	),
);
